import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'fe-hoc-connect';
import { store, updateSearchQuery, getSearchResults } from 'fe-appdir-data-app';
import { SvgIcon, Magnify } from 'fe-comp-svg-icon';
import { BouncingBars } from 'fe-comp-loader';
import theme, { visuallyHidden, notVisuallyHidden } from 'fe-appdir-lib-theme';

/* Accessibility:
 * Use a form with role='search' https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Search_role
 * Use an input type='search' https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/search
 * Use aria-label for invisible label https://webaim.org/techniques/forms/advanced
 * Provide a submit button (visually hidden in our case) https://www.w3.org/TR/WCAG20-TECHS/H32.html
 */

const PROP_TYPES = {
  placeholder: PropTypes.string,
  searchQuery: PropTypes.string.isRequired,
  isFetching: PropTypes.bool,
  onSubmit: PropTypes.func,
  gaEngagedCallback: PropTypes.func,
  gaSubmitCallback: PropTypes.func,
};

const DEFAULT_PROPS = {
  placeholder: 'Search',
  isFetching: false,
};

const THROTTLE_LIMIT_MS = 500;

const SearchForm = styled.form`
  position: relative;
  display: flex;
  font-family: ${theme.fontFamilyPrimary};

  @media (max-width: ${theme.breakpointSm}) {
    flex-shrink: 0;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: ${theme.spacingSm};
  width: ${(p) => (p.showLoader ? '16px' : null)};
  display: flex;
  align-items: center;
  pointer-events: none;

  @media (max-width: ${theme.breakpointSm}) {
    left: ${theme.spacingMd};
  }
`;

// Hide the default clear button rendered by input type='search'
const Input = styled.input`
  padding: 6px;
  padding-left: 36px;
  padding-right: ${theme.spacingMd};
  box-sizing: border-box;
  width: 100%;
  font-size: ${theme.fontSizeBase};
  background-color: ${theme.colorWhite};
  outline: none;
  border: 1px solid ${theme.colorGrey80};
  border-radius: 4px;
  -webkit-appearance: textfield; /* Safari's input[type="search"] is weird */

  &::placeholder {
    color: ${theme.colorGrey80};
  }

  &::-webkit-search-cancel-button {
    display: none;
  }
  &::-ms-clear {
    display: none;
  }

  &:focus {
    box-shadow: 0px 0px 10px 2px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: ${theme.breakpointSm}) {
    border: 0;
    font-size: ${theme.fontSizeXxl};
    border-radius: none;
    background: ${theme.colorGrey98};
    padding: 20px ${theme.spacingXxl};

    &:focus {
      box-shadow: none;
    }
  }
`;

// The submit button is initially hidden, but becomes visible when focused
// (inspired by Google's treatment of "skip to main content" link in search results)
const HiddenSubmitButton = styled.button`
  ${visuallyHidden()}

  &:focus {
    ${notVisuallyHidden()}
    position: absolute;
    top: ${theme.spacingXs};
    right: ${theme.spacingXs};
    bottom: ${theme.spacingXs};
    padding: 0 ${theme.spacingSm};
    font-size: ${theme.fontSizeBase};
  }
`;

class ThrottledSearchInput extends React.Component {
  constructor(props) {
    super(props);
    // for throttling
    this.isThrottled = false;
    this.savedQuery = null;
    // for analytics
    this.isEngaged = false;
  }

  // This function is called at the beginning of every throttled cycle
  // If a query was saved, get search results then start a new throttled cycle
  searchIfNeeded = () => {
    this.isThrottled = false;

    if (this.savedQuery !== null) {
      if (!this.props.isFetching) {
        getSearchResults(this.savedQuery);
        this.savedQuery = null;
      }
      // If still fetching, don't get search results yet; wait for another throttled cycle instead

      this.isThrottled = setTimeout(this.searchIfNeeded, THROTTLE_LIMIT_MS);
    }
  };

  // If not currently throttled, get search results and start a new throttled cycle
  // Otherwise, just save the query so that it can be read at the end of the current throttled cycle
  throttledGetSearchResults(query) {
    this.savedQuery = query;
    if (!this.isThrottled) this.searchIfNeeded();
  }

  onChange = (event) => {
    const query = event.target.value;
    updateSearchQuery(query);
    this.throttledGetSearchResults(query);

    // Send analytics hit when user starts typing for the first time
    if (!this.isEngaged && this.props.gaEngagedCallback) {
      this.props.gaEngagedCallback(query);
      this.isEngaged = true;
    }
  };

  onBlur = () => {
    this.isEngaged = false;
  };

  // Send analytics hit if needed & submit
  onSubmit = (event) => {
    if (this.props.gaSubmitCallback) this.props.gaSubmitCallback();
    this.props.onSubmit(event);
  };

  componentWillUnmount() {
    clearTimeout(this.isThrottled);
  }

  render = () => {
    const { placeholder, searchQuery, isFetching } = this.props;

    // Don't show the loader if the searchQuery is empty
    const showLoader = isFetching && searchQuery !== '';

    return (
      <SearchForm role="search" onSubmit={this.onSubmit}>
        <IconWrapper showLoader={showLoader}>
          {showLoader ? (
            <BouncingBars size={16} fill={theme.colorGrey20} />
          ) : (
            <SvgIcon glyph={Magnify} size={16} fill={theme.colorGrey20} />
          )}
        </IconWrapper>
        <Input
          type="search"
          aria-label="Search all apps"
          placeholder={placeholder}
          onChange={this.onChange}
          onBlur={this.onBlur}
          value={searchQuery}
        />
        <HiddenSubmitButton type="submit">Search</HiddenSubmitButton>
      </SearchForm>
    );
  };
}

ThrottledSearchInput.propTypes = PROP_TYPES;
ThrottledSearchInput.defaultProps = DEFAULT_PROPS;
ThrottledSearchInput.displayName = 'SearchInput';

const SearchInput = connect(
  store,
  (state) => ({
    searchQuery: state.searchQuery,
    isFetching: state.isFetchingAppsSearchResults,
  }),
)(ThrottledSearchInput);

export default SearchInput;
